import { useMemo, type ReactNode } from 'react';
import { format } from 'date-fns';
import { History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SentDateRange } from '@/components/shared/automation-reply-badges';
import { useSettingsLog } from '@/hooks/use-settings-log';
import { humanize, sentBetween, type DayRange } from '@/lib/automation-replies';
import { SettingsTableRows, StatusTag, type SettingsColumn } from '@/modules/settings';
import type { SettingsChange, SettingsLogEntry } from '@/services/settings-log.service';
import { SETTINGS_LOG_COLUMNS } from './settings-log-columns';

// `holdMessage.delaySeconds` → "hold message › delay seconds"
const fieldLabel = (path: string) =>
	path
		.split('.')
		.map((part) => part.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').toLowerCase())
		.join(' › ');

const valueLabel = (value: unknown): string => {
	if (value === null || value === undefined || value === '') return '—';
	if (typeof value === 'boolean') return value ? 'On' : 'Off';
	if (Array.isArray(value)) return value.length ? value.map(valueLabel).join(', ') : 'none';
	// A list item such as a channel's inbox reads by its value or name, not as raw JSON.
	if (typeof value === 'object') {
		const { value: inner, name, label } = value as Record<string, unknown>;
		return inner !== undefined || name !== undefined || label !== undefined ? valueLabel(inner ?? name ?? label) : JSON.stringify(value);
	}
	return String(value);
};

const changeText = (change: SettingsChange) => `${fieldLabel(change.path)}: ${valueLabel(change.before)} → ${valueLabel(change.after)}`;

const SETTINGS_LOG_CELLS: Record<string, (entry: SettingsLogEntry) => ReactNode> = {
	at: (entry) => <span className='truncate tabular-nums'>{format(new Date(entry.at), 'd MMM yyyy, HH:mm')}</span>,
	actor: (entry) =>
		entry.actor.type === 'system' ? (
			<span className='text-gray-900'>Kim</span>
		) : (
			<span className='truncate' title={entry.actor.email ?? undefined}>
				{entry.actor.name || entry.actor.email || 'Unknown user'}
			</span>
		),
	setting: (entry) => (
		<span className='flex min-w-0 items-center gap-2'>
			<span className='truncate'>
				{entry.area}
				{entry.subject && <span className='capitalize text-gray-900'> · {humanize(entry.subject)}</span>}
			</span>
			{entry.allBrands && <StatusTag tone='grey'>All brands</StatusTag>}
		</span>
	),
	// One line, like the other logs: the first change, and how many more (all of them on hover).
	change: (entry) => {
		if (entry.operation !== 'update') return <span>{entry.operation === 'create' ? 'Added' : 'Removed'}</span>;
		const [first, ...rest] = entry.changes;
		if (!first) return <span className='text-gray-700'>—</span>;
		return (
			<span className='flex min-w-0 items-center gap-2' title={entry.changes.map(changeText).join('\n')}>
				<span className='truncate'>
					<span className='text-gray-900'>{fieldLabel(first.path)}: </span>
					{valueLabel(first.before)} → {valueLabel(first.after)}
				</span>
				{rest.length > 0 && <span className='shrink-0 text-[11px] text-gray-800'>+{rest.length} more</span>}
			</span>
		);
	}
};

const SETTINGS_LOG_TABLE: SettingsColumn<SettingsLogEntry>[] = SETTINGS_LOG_COLUMNS.map((head) => ({
	...head,
	cell: SETTINGS_LOG_CELLS[head.id]
}));

/** Header tools: the dates the log covers. */
export function SettingsLogTools({ days, onDaysChange }: { days: DayRange; onDaysChange: (next: DayRange) => void }) {
	return <SentDateRange value={days} onChange={onDaysChange} className='w-[240px]' />;
}

/** Logs › Settings: who changed the brand's settings, and what changed, newest first. */
export function SettingsLog({ days }: { days: DayRange }) {
	const filter = useMemo(() => sentBetween(days), [days]);
	const { entries, isLoading, isError, loadMoreFailed, refetch, hasMore, loadingMore, loadMore } = useSettingsLog(filter);

	// A failed "Load older" keeps the rows already shown; only a failed first page replaces them.
	if (isError && entries.length === 0) {
		return (
			<div className='flex flex-col items-center gap-3 py-12'>
				<p className='text-[13px] text-gray-900'>The settings log could not be loaded.</p>
				<Button variant='outline' size='sm' onClick={() => void refetch()}>
					Retry
				</Button>
			</div>
		);
	}

	return (
		<>
			<SettingsTableRows
				columns={SETTINGS_LOG_TABLE}
				rows={entries}
				rowKey={(entry) => entry.id}
				isLoading={isLoading}
				empty={
					<div className='flex flex-col items-center gap-3'>
						<span className='flex size-10 items-center justify-center rounded-full bg-indigo-50'>
							<History className='size-4 text-indigo-600' />
						</span>
						<p className='text-[13px] font-medium text-ink'>{days.startDate ? 'Nothing matches' : 'No settings changes yet'}</p>
						<p className='max-w-[420px] text-[12px] leading-4 text-gray-900'>
							{days.startDate ? 'Try other dates.' : 'When someone changes this brand’s settings, it shows here with who did it and what changed.'}
						</p>
					</div>
				}
			/>
			{hasMore && (
				<div className='flex justify-center py-4'>
					<Button variant='outline' size='sm' onClick={loadMore} disabled={loadingMore} className='rounded-full'>
						{loadingMore ? 'Loading…' : loadMoreFailed ? 'Could not load more. Retry' : 'Load older changes'}
					</Button>
				</div>
			)}
		</>
	);
}
